import * as React from 'react';
import {
  Field,
  FieldArray,
  InjectedFormProps,
  reduxForm,
  WrappedFieldArrayProps,
  WrappedFieldProps,
} from 'redux-form';
import { DatePicker } from 'material-ui-pickers';
import { Button, Grid, IconButton, TextField, withTheme, WithTheme } from '@material-ui/core';
import { Delete } from '@material-ui/icons';

import { formatFieldValue, getInputAdornment } from '../../../shared/format';
import { decimal, required } from '../../../shared/Forms/validations';
import { PaymentSchedule } from '../../../../models/debt';
import * as styles from './SettlementScheduleForm.css';

export type SettlementScheduleFormProps = EnteredSettlementScheduleFormProps &
  InjectedFormProps<{}, EnteredSettlementScheduleFormProps> &
  WithTheme;

interface EnteredSettlementScheduleFormProps {
  submitPayment: (payment: PaymentSchedule) => void;
  goBack: () => void;
  isMobile: boolean;
}

const SettlementScheduleForm = ({
  handleSubmit,
  isMobile,
  theme,
  submitting,
  submitPayment,
  goBack,
}: SettlementScheduleFormProps) => (
  <Grid item xs={12} container justify="center" style={isMobile ? { fontSize: '12px' } : {}}>
    <Grid item xs={12} sm={8} lg={6} container justify="center">
      <form style={{ width: '100%' }} onSubmit={(event) => handleSubmit(event)}>
        <Grid container justify="space-between" style={{ textAlign: 'center' }}>
          <Grid item xs={1} />
          <Grid item xs={3} container justify="center">
            <b>Date</b>
          </Grid>
          <Grid item xs={4} container justify="center">
            <b>Amount Due</b>
          </Grid>
          <Grid item xs={4} container justify="center">
            <b>Amount Paid Off</b>
          </Grid>
        </Grid>
        <FieldArray
          theme={theme}
          name="schedule"
          isMobile={isMobile}
          submitPayment={submitPayment}
          component={RenderSchedule as any}
        />
        <Grid container justify="center">
          <span>
            <Button className={styles.cancel} onClick={() => goBack()}>
              Cancel
            </Button>
            <Button
              size="large"
              variant="contained"
              color="primary"
              type="submit"
              disabled={submitting}>
              Submit
            </Button>
          </span>
        </Grid>
      </form>
    </Grid>
  </Grid>
);

interface RenderScheduleProps {
  isMobile: boolean;
  submitPayment: (payment: PaymentSchedule) => void;
}

const lessThanTotal = (value: string, allValues: any, props: any, input: any) => {
  // Not proud of this. But unsure of how to validate by passing the paymentAmount param
  // without causing an infinite loop ~ https://github.com/erikras/redux-form/issues/4148
  const index = input ? input.split('[')[1].split(']')[0] : 0;
  if (allValues.schedule[index]) {
    const paymentAmount = allValues.schedule[index].paymentAmount;
    if (value && Number(value) > Number(paymentAmount)) {
      return 'Cannot exceed Amount Due';
    }
  }
};

const RenderSchedule = ({
  fields,
  theme,
  isMobile,
}: WrappedFieldArrayProps<PaymentSchedule & { editable?: boolean }> &
  RenderScheduleProps &
  WithTheme) => (
  <>
    {fields.map((member, index) => (
      <Grid key={index} container justify="space-between" alignItems="center">
        <Grid item xs={1} container justify="center">
          <IconButton
            type="button"
            style={{ padding: isMobile ? '0' : '12px' }}
            onClick={() => fields.remove(index)}>
            <Delete />
          </IconButton>
        </Grid>
        <Grid item xs={3} container justify="center">
          <Field
            name={`${member}.paymentDueDate`}
            isMobile={isMobile}
            disabled={!fields.get(index).editable}
            component={DateField}
            format={(date) => {
              const dateObj = new Date(date);
              // https://stackoverflow.com/a/14569783/8249733
              return new Date(dateObj.getTime() - dateObj.getTimezoneOffset() * -60000);
            }}
            validate={required}
          />
        </Grid>
        <Grid item xs={4} container justify="center">
          <Field
            name={`${member}.paymentAmount`}
            disabled={!fields.get(index).editable}
            isMobile={isMobile}
            component={ScheduleField}
            validate={[required, decimal]}
            parse={(value) => (value ? Number(value) : value)}
          />
        </Grid>
        <Grid item xs={4} container justify="center">
          <Field
            name={`${member}.paidOff`}
            isMobile={isMobile}
            disabled={false}
            component={ScheduleField}
            validate={[decimal, lessThanTotal]}
            parse={(value) => (value ? Number(value) : value)}
          />
        </Grid>
      </Grid>
    ))}
    <Grid container justify="center">
      <Grid item xs={8} sm={6}>
        <Button
          className={styles.addMoreButton}
          size="large"
          fullWidth
          variant="outlined"
          color="primary"
          onClick={() =>
            fields.push({
              paymentDueDate: new Date(),
              paymentAmount: 0,
              paidOff: 0,
              editable: true,
            } as any)
          }>
          Add More
        </Button>
      </Grid>
    </Grid>
  </>
);

const DateField = (props: WrappedFieldProps & { isMobile: boolean; disabled: boolean }) => (
  <Grid container justify="center">
    {props.disabled ? (
      <span>{props.input.value.toLocaleDateString()}</span>
    ) : (
      <DatePicker
        {...props.input}
        format="MM/dd/yyyy"
        disabled={props.disabled}
        // @ts-ignore
        ismobile={props.isMobile.toString()}
        TextFieldComponent={CustomTextField}
      />
    )}
    {props.meta.touched && props.meta.error && <span>{props.meta.error}</span>}
  </Grid>
);
const CustomTextField = (props: any) => (
  <TextField
    {...props}
    InputProps={props.ismobile === 'true' ? { style: { fontSize: '12px', width: '74px' } } : {}}
    inputProps={props.ismobile === 'true' ? { style: { padding: '9px 0 7px' } } : {}}
    margin="normal"
    className={styles.field}
    variant="outlined"
  />
);

const ScheduleField = (props: WrappedFieldProps & { disabled: boolean; isMobile: boolean }) => (
  <Grid container alignItems="center" justify="center">
    {props.disabled ? (
      <span>{formatFieldValue(props.input.value, 'number', 'dollar')}</span>
    ) : (
      <>
        <TextField
          {...props.input}
          type="number"
          margin="normal"
          disabled={props.disabled}
          InputProps={{
            ...getInputAdornment('dollar'),
            style: props.isMobile ? { fontSize: '12px' } : {},
          }}
          className={styles.field}
          variant="outlined"
        />
        {props.meta.touched && props.meta.error && <span>{props.meta.error}</span>}
      </>
    )}
  </Grid>
);

export default withTheme()(
  reduxForm<{}, EnteredSettlementScheduleFormProps>({
    form: 'settlementScheduleForm',
  })(SettlementScheduleForm)
);
